import { INSTANCE as DeviceConnectionKeeper } from '@core/devices/DeviceConnectionKeeper'
import { TaskError, TaskState } from '@core/transmission/ITask'
import { HttpRequestCancelToken } from '@core/HttpRequestCancelToken'
import { AbsUploadTask } from '@core/transmission/AbsUploadTask'
import { ILLEGAL_FILE_SIZE } from './AbsTask'
import { getFileMd5 } from '../FileUtils'

const EMPTY_FILE_SIZE = 0

/**
 * 上传文件任务
 */
export class UploadTaskDirectly extends AbsUploadTask {
  #transmittedTotal = 0
  #requestCancelToken: HttpRequestCancelToken | undefined

  constructor(
    deviceId: string,
    targetPath: string,
    file: File,
    isViaLan: boolean,
    relativePath: string,
    appId: string
  ) {
    super(deviceId, targetPath, file, isViaLan, relativePath, appId)
    this._noticeId = ''
  }

  async start(): Promise<void> {
    const device = this._getDevice()

    if (!device) {
      this._terminateDueToDeviceNotFound()

      return
    }

    try {
      this.#requestCancelToken = new HttpRequestCancelToken()
      const fileMd5 = await getFileMd5(this._file!, this.#requestCancelToken, () => {
        DeviceConnectionKeeper.keepDeviceConnectById(this._deviceId)
        this._taskScheduler?.updateTask(this)
      })

      await device.uploadFile(this._targetPath, this._file!, this._progressListener, this.#requestCancelToken, fileMd5)
    } catch (error) {
      console.log(
        `[Transmission]: Lan uploadFile start error fileName: ${this._fileName} ${new Date().toLocaleString()}`,
        error
      )
      this._failedReason = TaskError.TRANSFER_FAILED
      this._handleError(error)
    }
  }

  async pause(): Promise<void> {
    try {
      this.#requestCancelToken?.cancel()
    } catch (error) {
      this._failedReason = TaskError.TRANSFER_FAILED

      this._handleError(error)
    }
  }

  async resume(): Promise<void> {
    try {
      const device = this._getDevice()

      if (!device) {
        this._terminateDueToDeviceNotFound()

        return
      }

      this.start()
    } catch (error) {
      this._failedReason = TaskError.TRANSFER_FAILED

      this._handleError(error)
    }
  }

  async onRemove(): Promise<boolean> {
    try {
      this.markAsDeleted()
      this.#requestCancelToken?.cancel()
      this.#requestCancelToken = undefined

      return true
    } catch (error) {
      this._failedReason = TaskError.TRANSFER_FAILED

      this._handleError(error)

      return false
    }
  }

  onProgressChanged(transmitted: number, total: number): void {
    if (this._state === TaskState.FAILED || this._state === TaskState.SUCCESS) return
    if (transmitted <= this._transmitted) return

    this._transmitted = transmitted
    this.#transmittedTotal = total
    this._taskScheduler?.updateTask(this)
    this._fileUploadListener?.onProgressChanged?.(this._transmitted, this.#transmittedTotal, this._deviceId, false)
  }

  onCompleted(): void {
    //TODO: 待分析 已传量小于文件量时调用了完成的回调
    this._transmitted = this._fileSize
    this.#transmittedTotal = this._fileSize
    this._taskScheduler?.setTaskSucceed(this)
    this._fileUploadListener?.onSuccess(this._deviceId, this._targetPath, this._fileName)
    this.#requestCancelToken = undefined
  }

  protected _getCompletionRation(): number {
    //文件大小为 0B
    if (this.state === TaskState.SUCCESS && this.#transmittedTotal === EMPTY_FILE_SIZE) return 100

    if (this.#transmittedTotal === ILLEGAL_FILE_SIZE || this.#transmittedTotal === EMPTY_FILE_SIZE) return 0

    return Math.floor((this._transmitted / this.#transmittedTotal) * 100)
  }

  protected _handleError(error: unknown): void {
    super._handleError(error)
    this.#requestCancelToken = undefined
  }
}
